import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CalendarClock, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { metaApi } from './MetaAdAccountsCard';

const SYNC_MODES = [['off', 'Off'], ['daily', 'Daily'], ['every_12h', 'Every 12 hours'], ['every_6h', 'Every 6 hours'], ['weekly', 'Weekly']];
const STATUS_MODES = [['off', 'Off'], ['hourly', 'Every hour'], ['every_3h', 'Every 3 hours']];
const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const hourLabel = (h) => `${h % 12 || 12}:00 ${h < 12 ? 'AM' : 'PM'}`;
const when = (iso) => (iso ? new Date(iso).toLocaleString('en-IN', {
  timeZone: 'Asia/Kolkata', weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit',
}) : '—');

const Pick = ({ label, value, onChange, options, disabled, testId, width = 'w-40' }) => (
  <div className="space-y-1">
    <Label className="text-xs text-slate-500">{label}</Label>
    <Select value={String(value)} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger className={`${width} h-9`} data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent>
        {options.map(([v, l]) => <SelectItem key={v} value={String(v)}>{l}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);

export const MetaSyncSchedule = ({ isAdmin }) => {
  const [sch, setSch] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  useEffect(() => { metaApi('/meta-sync/schedule').then(setSch).catch((e) => toast.error(e.message)); }, []);
  if (!sch) return null;

  const set = (patch) => { setSch({ ...sch, ...patch }); setDirty(true); };
  const save = async () => {
    setSaving(true);
    try {
      const { sync_mode, hour, weekday, status_mode } = sch;
      setSch(await metaApi('/meta-sync/schedule', { method: 'PUT', body: JSON.stringify({ sync_mode, hour, weekday, status_mode }) }));
      setDirty(false);
      toast.success('Auto-sync schedule saved');
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };
  const usesTime = sch.sync_mode !== 'off';
  const hours = Array.from({ length: 24 }, (_, h) => [h, hourLabel(h)]);

  return (
    <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50/60 p-4" data-testid="meta-sync-schedule">
      <div className="mb-3 flex items-center gap-2">
        <CalendarClock className="h-4 w-4 text-[#2EB4AC]" />
        <span className="text-sm font-semibold text-slate-800">Auto-sync schedule</span>
        <span className="text-xs text-slate-400">(IST)</span>
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <Pick label="ROAS + live status sync" value={sch.sync_mode} onChange={(v) => set({ sync_mode: v })} options={SYNC_MODES} disabled={!isAdmin} testId="schedule-sync-mode" />
        {sch.sync_mode === 'weekly' && (
          <Pick label="On" value={sch.weekday} onChange={(v) => set({ weekday: Number(v) })} options={DAYS.map((d, i) => [i, d])} disabled={!isAdmin} testId="schedule-weekday" width="w-36" />
        )}
        {usesTime && (
          <Pick label={sch.sync_mode.startsWith('every') ? 'Starting at' : 'At'} value={sch.hour} onChange={(v) => set({ hour: Number(v) })} options={hours} disabled={!isAdmin} testId="schedule-hour" width="w-32" />
        )}
        <Pick label="Live status refresh in between" value={sch.status_mode} onChange={(v) => set({ status_mode: v })} options={STATUS_MODES} disabled={!isAdmin} testId="schedule-status-mode" />
        {isAdmin && (
          <Button size="sm" onClick={save} disabled={!dirty || saving} className="h-9 bg-[#2EB4AC] hover:bg-[#25908A]" data-testid="schedule-save-btn">
            {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Save schedule
          </Button>
        )}
      </div>
      <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-500">
        <span data-testid="schedule-next-sync">Next sync: <b className="text-slate-700">{sch.sync_mode === 'off' ? 'Off' : when(sch.next_sync_at)}</b></span>
        <span data-testid="schedule-next-status">Next status refresh: <b className="text-slate-700">{sch.status_mode === 'off' ? 'Off' : when(sch.next_status_at)}</b></span>
        {sch.last_auto_sync_at && <span>Last auto sync: {when(sch.last_auto_sync_at)}</span>}
      </div>
      <p className="mt-2 text-[11px] text-slate-400">Every sync (manual, selected or scheduled) also updates live status. Changes apply from the next scheduled slot.</p>
    </div>
  );
};
